import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Text,
  Container,
  Card,
} from "@chakra-ui/react";
import { Field } from "@chakra-ui/react";

import { useAppDispatch, useAppSelector } from "../store/hooks";
import { verifyEmail, resendVerification } from "../store/slices/authSlice";
import { toaster } from "../components/ui/toaster";

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);
  const emailFromState = location.state?.email || "";

  const [formData, setFormData] = useState({
    email: emailFromState,
    code: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.code.length !== 7) {
      toaster.create({
        title: "Code must be 7 digits",
        type: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const result = await dispatch(verifyEmail(formData)).unwrap();
      toaster.create({
        title: "Email verified",
        description: result.message,
        type: "success",
        duration: 3000,
      });
      navigate("/login");
    } catch (error: any) {
      toaster.create({
        title: "Verification failed",
        description: error || "Invalid code",
        type: "error",
        duration: 5000,
      });
    }
  };

  const handleResend = async () => {
    try {
      const result = await dispatch(
        resendVerification({ email: formData.email }),
      ).unwrap();
      toaster.create({
        title: "Code sent",
        description: result.message,
        type: "success",
        duration: 3000,
      });
    } catch (error: any) {
      toaster.create({
        title: "Failed to resend",
        description: error,
        type: "error",
        duration: 3000,
      });
    }
  };

  return (
    <Box className="min-h-screen flex items-center justify-center" bg="gray.50">
      <Container maxW="md" py={12}>
        <VStack mb={6} textAlign="center">
          <Heading size="xl">Verify Email</Heading>
          <Text color="gray.600">
            Enter the 7-digit code sent to your email
          </Text>
        </VStack>

        <Card.Root>
          <Card.Body p={8}>
            <form onSubmit={handleSubmit}>
              <VStack gap={4}>
                <Field.Root required width="full">
                  <Field.Label>Email</Field.Label>
                  <Input
                    type="email"
                    value={formData.email}
                    readOnly
                    bg="gray.100"
                  />
                </Field.Root>

                <Field.Root required width="full">
                  <Field.Label>Verification Code</Field.Label>
                  <Input
                    placeholder="1234567"
                    maxLength={7}
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    required
                  />
                </Field.Root>

                <Button
                  type="submit"
                  colorScheme="blue"
                  width="full"
                  loading={loading}
                  mt={2}>
                  Verify Email
                </Button>

                <Button
                  variant="outline"
                  onClick={handleResend}
                  width="full"
                  color="blue.600"
                  borderColor="blue.600">
                  Resend Code
                </Button>

                <Text textAlign="center">
                  <Link
                    to="/login"
                    className="text-blue-500 hover:text-blue-600">
                    Back to login
                  </Link>
                </Text>
              </VStack>
            </form>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
